import type { AlertProps } from '@mui/material'
import { Alert, Button } from '@mui/material'
import { graphHooks } from '@tunasong/graph-lib/react'
import { features, useThunkDispatch } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isPersisted } from '@tunasong/schemas'
import type { FC } from 'react'
import { useCallback, useEffect, useEffectEvent, useState } from 'react'
import ConfirmDialog from '../dialog/confirm-dialog.js'
import { useNavigateToEntity } from '../navigation/navigate.js'
export interface TrashHeaderProps extends AlertProps {
  className?: string
  entity?: Persisted<Entity>
}

export const TrashHeader: FC<TrashHeaderProps> = props => {
  const { className, entity, ...alertProps } = props
  const dispatch = useThunkDispatch()
  const { entity: parent } = graphHooks.useEntity(entity?.parentId)
  const navigate = useNavigateToEntity()

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const updateEntity = graphHooks.useEntityUpdate()

  const handleShowDelete = () => setShowDeleteDialog(true)
  const handleHideDelete = () => setShowDeleteDialog(false)

  const [deleteEntity, deleteEntityResult] = graphHooks.useEntityDelete()

  const handleDelete = async () => {
    if (!entity) {
      return
    }
    await deleteEntity({ entity })
    /** Navigate to the parent (if it exists) */
    if (parent) {
      navigate(parent)
    }

    setShowDeleteDialog(false)
  }

  const errorAlertEvent = useEffectEvent(() => {
    dispatch(
      features.notifications.actions.setAlert({
        message: `Failed to delete entity: ${deleteEntityResult.originalArgs?.entity.id}`,
        severity: 'error',
      })
    )
  })

  useEffect(() => {
    if (!deleteEntityResult.isError) {
      return
    }
    errorAlertEvent()
  }, [deleteEntityResult.isError])

  const handleRestore = useCallback(() => {
    if (!isPersisted(entity)) {
      return
    }
    updateEntity(entity.id, { trash: false })
  }, [entity, updateEntity])

  if (!entity?.trash) {
    return null
  }

  return (
    <>
      <Alert
        className={className}
        severity="error"
        action={
          <>
            <Button onClick={handleRestore}>Restore</Button>
            <Button onClick={handleShowDelete}>Delete Permanently</Button>
          </>
        }
        {...alertProps}
      >
        This entity is in the Trash
      </Alert>
      <ConfirmDialog
        open={showDeleteDialog}
        text={`Really permanently delete ${entity.type}: ${entity.name}? This action is non-reversable.`}
        onCancel={handleHideDelete}
        onConfirm={handleDelete}
      />
    </>
  )
}

export default TrashHeader
